import { useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Clock3 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Logo, BRAND_NAME } from "../components/ui/Logo";
import { getResourceBySlug } from "../lib/resourcesData";

const APP_LOGIN_URL = process.env.REACT_APP_APP_URL || "https://app.aurorageriatrica.com/login";
const getTrialUrl = () =>
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
    ? "/login"
    : APP_LOGIN_URL;

export default function ResourceArticle() {
  const { slug } = useParams();
  const article = useMemo(() => getResourceBySlug(slug), [slug]);

  if (!article) return <Navigate to="/recursos" replace />;

  return (
    <div className="min-h-screen bg-white text-slate-800">
      <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-2 font-semibold text-slate-900">
            <Logo variant="icon" size="md" className="h-8 w-8" alt="" />
            {BRAND_NAME}
          </Link>
          <Link to="/recursos" className="text-sm text-slate-600 hover:text-slate-900">
            Recursos
          </Link>
        </div>
      </header>

      <main className="px-4 py-10 sm:px-6 lg:px-8">
        <article className="mx-auto max-w-3xl">
          <Link to="/recursos" className="inline-flex items-center text-sm font-medium text-slate-600 hover:text-slate-900">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Volver a Recursos
          </Link>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600">
              {article.category}
            </Badge>
            <span className="inline-flex items-center text-sm text-slate-500">
              <Clock3 className="mr-1.5 h-4 w-4" />
              {article.readTime}
            </span>
          </div>

          <h1 className="mt-4 text-3xl font-semibold leading-tight tracking-tight text-slate-900 sm:text-4xl">
            {article.title}
          </h1>
          <p className="mt-4 text-lg leading-relaxed text-slate-600">{article.summary}</p>

          <section className="mt-8 rounded-xl border border-slate-200 bg-slate-50/60 p-5">
            <p className="text-sm leading-relaxed text-slate-700">{article.intro}</p>
          </section>

          <div className="mt-10 space-y-8">
            {article.sections.map((section) => (
              <section key={section.heading}>
                <h2 className="text-xl font-semibold text-slate-900">{section.heading}</h2>
                <div className="mt-3 space-y-3">
                  {section.body.map((paragraph) => (
                    <p key={paragraph} className="text-[15px] leading-relaxed text-slate-700">
                      {paragraph}
                    </p>
                  ))}
                </div>
                {section.image && (
                  <figure className="my-6">
                    <img
                      src={section.image}
                      alt={section.imageAlt || section.heading}
                      className="w-full rounded-xl border border-slate-200 object-cover shadow-sm"
                      loading="lazy"
                    />
                  </figure>
                )}
              </section>
            ))}
          </div>

          <section className="mt-10 border-t border-slate-100 pt-6">
            <p className="text-[15px] leading-relaxed text-slate-700">{article.closing}</p>
          </section>

          {article.preCtaImage && (
            <figure className="my-8">
              <img
                src={article.preCtaImage}
                alt={article.preCtaImageAlt || "Consulta geriatrica con revision de informe clinico"}
                className="w-full rounded-xl border border-slate-200 object-cover shadow-sm"
                loading="lazy"
              />
            </figure>
          )}

          <Card className="mt-10 border-sky-200/70 bg-gradient-to-b from-sky-50/70 to-white shadow-sm">
            <CardContent className="p-6 sm:p-7">
              <h3 className="text-xl font-semibold tracking-tight text-slate-900">
                {article.ctaTitle || "Organice el expediente geriátrico con Aurora"}
              </h3>
              {article.ctaBullets?.length ? (
                <ul className="mt-3 space-y-1.5 text-sm text-slate-700">
                  {article.ctaBullets.map((bullet) => (
                    <li key={bullet} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-emerald-600" />
                      {bullet}
                    </li>
                  ))}
                </ul>
              ) : null}
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Consulte el historial, registre consultas estructuradas y genere informes profesionales para el seguimiento.
              </p>
              <div className="mt-5 flex flex-col gap-2 sm:flex-row">
                <a href={getTrialUrl()}>
                  <Button className="w-full bg-sky-600 hover:bg-sky-700 text-white sm:w-auto">
                    Probar gratis
                  </Button>
                </a>
                <Link to="/demo-clinico">
                  <Button variant="outline" className="w-full border-slate-300 sm:w-auto">
                    Ver demostracion
                    <ArrowRight className="ml-1.5 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </article>
      </main>
    </div>
  );
}
